import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { colorBackgroundBody, colorBlue, colorText } from '@spark/ui-components/theme';

const API_SYNC_URL = process.env.REACT_APP_BASE_API_URL_SYNC || 'http://localhost:32768';

const pickQueryValue = (params, key) =>
  params.get(key) || params.get(key.toLowerCase()) || params.get(key.toUpperCase()) || '';

const sanitizeDigits = (value = '') => String(value).replace(/\D/g, '');

const formatCnpjInput = (value = '') => {
  const digits = sanitizeDigits(value).slice(0, 14);
  return digits
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
};

const toDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(date);
};

const isImageUrl = (url = '') => {
  if (!url) return false;
  const lower = url.toLowerCase();
  return lower.startsWith('data:image/')
    || lower.endsWith('.png')
    || lower.endsWith('.jpg')
    || lower.endsWith('.jpeg')
    || lower.endsWith('.gif')
    || lower.endsWith('.webp')
    || lower.endsWith('.bmp')
    || lower.endsWith('.svg');
};

const resolveTipoNotaLabel = (tipoNota, descricao) => {
  if (descricao) return descricao;
  if (Number(tipoNota) === 2) return 'Devolução';
  return 'Normal';
};

const mapResponse = (payload = {}) => {
  const registrosRaw = Array.isArray(payload.registros)
    ? payload.registros
    : Array.isArray(payload.Registros)
      ? payload.Registros
      : [];

  return {
    cnpjCliente: String(payload.cnpjCliente || payload.CNPJCliente || ''),
    numeroNF: String(payload.numeroNF || payload.NumeroNF || ''),
    registros: registrosRaw.map((item) => ({
      id: Number(item.id || item.Id || 0),
      numeroNF: String(item.numeroNF || item.NumeroNF || ''),
      cnpjCliente: String(item.cnpjCliente || item.CNPJCliente || ''),
      tipoNota: Number(item.tipoNota || item.TipoNota || 1),
      tipoNotaDescricao: String(item.tipoNotaDescricao || item.TipoNotaDescricao || ''),
      statusProcesso: String(item.statusProcesso || item.StatusProcesso || ''),
      dataChegada: item.dataChegada || item.DataChegada || null,
      dataLeitura: item.dataLeitura || item.DataLeitura || null,
      dataAgendamento: item.dataAgendamento || item.DataAgendamento || null,
      dataSaida: item.dataSaida || item.DataSaida || null,
      dataEnvioCanhoto: item.dataEnvioCanhoto || item.DataEnvioCanhoto || null,
      quantidadeTotalCaixas: item.quantidadeTotalCaixas ?? item.QuantidadeTotalCaixas ?? null,
      quantidadeTotalPaletes: item.quantidadeTotalPaletes ?? item.QuantidadeTotalPaletes ?? null,
      quantidadeTotalEtiquetas: item.quantidadeTotalEtiquetas ?? item.QuantidadeTotalEtiquetas ?? null,
      finalizado: item.finalizado ?? item.Finalizado ?? null,
      descricaoDivergencia: String(item.descricaoDivergencia || item.DescricaoDivergencia || ''),
      urlImagemCanhoto: String(item.urlImagemCanhoto || item.UrlImagemCanhoto || ''),
      urlsAnexos: Array.isArray(item.urlsAnexos)
        ? item.urlsAnexos.filter(Boolean)
        : Array.isArray(item.UrlsAnexos)
          ? item.UrlsAnexos.filter(Boolean)
          : [],
    })),
  };
};

const AcompanhamentoPublicoDeposito = () => {
  const location = useLocation();
  const history = useHistory();

  const [cnpj, setCnpj] = useState('');
  const [nf, setNf] = useState('');
  const [token, setToken] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [resultado, setResultado] = useState(null);

  const canSubmit = useMemo(() => (
    sanitizeDigits(cnpj).length > 0 && nf.trim().length > 0 && token.trim().length > 0
  ), [cnpj, nf, token]);

  const validar = useCallback((nextCnpj, nextNf, nextToken) => {
    const validationErrors = {};
    if (sanitizeDigits(nextCnpj).length !== 14) {
      validationErrors.cnpj = 'Informe um CNPJ válido com 14 dígitos.';
    }
    if (!nextNf.trim()) {
      validationErrors.nf = 'Informe o número da Nota Fiscal.';
    }
    if (!nextToken.trim()) {
      validationErrors.token = 'Informe o token de acesso.';
    }
    return validationErrors;
  }, []);

  const consultar = useCallback(async (cnpjDigits, nfValue, tokenValue) => {
    setLoading(true);
    setErrorMessage('');
    setResultado(null);

    try {
      const endpoint = new URL('/api/v1/DepositoNotaFiscal/acompanhamento-publico', API_SYNC_URL);
      endpoint.searchParams.set('CNPJ', cnpjDigits);
      endpoint.searchParams.set('NF', nfValue);
      endpoint.searchParams.set('TOKEN', tokenValue);

      const response = await fetch(endpoint.toString(), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      });

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const payload = await response.json();
        if (!response.ok) {
          throw new Error(payload?.error || 'Não foi possível consultar o acompanhamento.');
        }
        setResultado(mapResponse(payload));
        return;
      }

      const payloadText = await response.text();
      throw new Error(payloadText || 'Resposta inválida da API de acompanhamento.');
    } catch (error) {
      setErrorMessage(error?.message || 'Erro inesperado ao consultar acompanhamento.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const queryCnpj = sanitizeDigits(pickQueryValue(params, 'CNPJ'));
    const queryNf = pickQueryValue(params, 'NF').trim();
    const queryToken = pickQueryValue(params, 'TOKEN').trim();

    if (!queryCnpj && !queryNf && !queryToken) {
      return;
    }

    setCnpj(formatCnpjInput(queryCnpj));
    setNf(queryNf);
    setToken(queryToken);

    if (queryCnpj && queryNf && queryToken) {
      const validationErrors = validar(queryCnpj, queryNf, queryToken);
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length === 0) {
        consultar(queryCnpj, queryNf, queryToken);
      }
    }
  }, [consultar, location.search, validar]);

  const onSubmit = useCallback((event) => {
    event.preventDefault();

    const cnpjDigits = sanitizeDigits(cnpj);
    const nfValue = nf.trim();
    const tokenValue = token.trim();
    const validationErrors = validar(cnpjDigits, nfValue, tokenValue);
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    const searchParams = new URLSearchParams({
      CNPJ: cnpjDigits,
      NF: nfValue,
      TOKEN: tokenValue,
    });

    history.replace({
      pathname: '/acompanhamento-deposito',
      search: `?${searchParams.toString()}`,
    });
  }, [cnpj, history, nf, token, validar]);

  return (
    <PageContainer>
      <ContentCard>
        <Title>Acompanhamento</Title>
        <Subtitle>
          O Token é o código de acompanhamento que você recebeu, caso não tenha, entre em contato com a nossa equipe.
        </Subtitle>

        <Form onSubmit={onSubmit}>
          <FieldGroup>
            <FieldLabel>CNPJ</FieldLabel>
            <FieldInput
              type="text"
              value={cnpj}
              maxLength={18}
              placeholder="00.000.000/0000-00"
              onChange={(event) => setCnpj(formatCnpjInput(event.target.value))}
            />
            {errors.cnpj ? <FieldError>{errors.cnpj}</FieldError> : null}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Número da Nota Fiscal</FieldLabel>
            <FieldInput
              type="text"
              value={nf}
              placeholder="Ex.: 123456"
              onChange={(event) => setNf(event.target.value)}
            />
            {errors.nf ? <FieldError>{errors.nf}</FieldError> : null}
          </FieldGroup>

          <FieldGroup>
            <FieldLabel>Token de acesso</FieldLabel>
            <FieldInput
              type="text"
              value={token}
              placeholder="Token de rastreamento"
              onChange={(event) => setToken(event.target.value)}
            />
            {errors.token ? <FieldError>{errors.token}</FieldError> : null}
          </FieldGroup>

          <Actions>
            <SubmitButton type="submit" disabled={!canSubmit || loading}>
              {loading ? 'Consultando...' : 'Consultar'}
            </SubmitButton>
          </Actions>
        </Form>

        {errorMessage ? <ApiError>{errorMessage}</ApiError> : null}

        {resultado ? (
          <>

            <CardsGrid>
              {resultado.registros.map((registro) => (
                <TrackingCard key={`${registro.id}-${registro.numeroNF}`}>
                  <StatusRow>
                    <FinalizadoChip $done={registro.finalizado === true}>
                      {registro.finalizado === true ? 'Finalizado' : 'Em andamento'}
                    </FinalizadoChip>
                    <TipoNotaChip $devolucao={Number(registro.tipoNota) === 2}>
                      {resolveTipoNotaLabel(registro.tipoNota, registro.tipoNotaDescricao)}
                    </TipoNotaChip>
                  </StatusRow>

                  <CardData>
                    <span><strong>NF:</strong> {registro.numeroNF || '-'}</span>
                    <span><strong>CNPJ:</strong> {registro.cnpjCliente || '-'}</span>
                    <span><strong>Data Chegada:</strong> {toDateTime(registro.dataChegada)}</span>
                    <span><strong>Data Leitura:</strong> {toDateTime(registro.dataLeitura)}</span>
                    <span><strong>Data Agendamento:</strong> {toDateTime(registro.dataAgendamento)}</span>
                    <span><strong>Data Saída:</strong> {toDateTime(registro.dataSaida)}</span>
                    <span><strong>Data Envio Canhoto:</strong> {toDateTime(registro.dataEnvioCanhoto)}</span>
                    {registro.quantidadeTotalCaixas !== null && registro.quantidadeTotalCaixas !== undefined ? (
                      <span><strong>Qtd. Caixas:</strong> {registro.quantidadeTotalCaixas}</span>
                    ) : null}
                    {registro.quantidadeTotalPaletes !== null && registro.quantidadeTotalPaletes !== undefined ? (
                      <span><strong>Qtd. Paletes:</strong> {registro.quantidadeTotalPaletes}</span>
                    ) : null}
                    {registro.quantidadeTotalEtiquetas !== null && registro.quantidadeTotalEtiquetas !== undefined ? (
                      <span><strong>Qtd. Etiquetas:</strong> {registro.quantidadeTotalEtiquetas}</span>
                    ) : null}
                    {registro.descricaoDivergencia ? (
                      <DivergenciaBox>
                        <strong>Divergência:</strong> {registro.descricaoDivergencia}
                      </DivergenciaBox>
                    ) : null}
                  </CardData>

                  {registro.urlImagemCanhoto ? (
                    <MediaSection>
                      <MediaTitle>Canhoto</MediaTitle>
                      <MediaGrid>
                        {isImageUrl(registro.urlImagemCanhoto) ? (
                          <MediaThumbLink href={registro.urlImagemCanhoto} target="_blank" rel="noreferrer">
                            <MediaThumb src={registro.urlImagemCanhoto} alt="Canhoto" />
                          </MediaThumbLink>
                        ) : (
                          <MediaLink href={registro.urlImagemCanhoto} target="_blank" rel="noreferrer">
                            Abrir canhoto
                          </MediaLink>
                        )}
                      </MediaGrid>
                    </MediaSection>
                  ) : null}

                  {registro.urlsAnexos?.length ? (
                    <MediaSection>
                      <MediaTitle>Imagens / Anexos</MediaTitle>
                      <MediaGrid>
                        {registro.urlsAnexos.map((url, index) => (
                          isImageUrl(url) ? (
                            <MediaThumbLink key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer">
                              <MediaThumb src={url} alt={`Anexo ${index + 1}`} />
                            </MediaThumbLink>
                          ) : (
                            <MediaLink key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer">
                              Abrir anexo {index + 1}
                            </MediaLink>
                          )
                        ))}
                      </MediaGrid>
                    </MediaSection>
                  ) : null}
                </TrackingCard>
              ))}
            </CardsGrid>
          </>
        ) : null}
      </ContentCard>
    </PageContainer>
  );
};

export default AcompanhamentoPublicoDeposito;

const PageContainer = styled.div`
  min-height: 100vh;
  background: ${colorBackgroundBody};
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 32px 16px;
`;

const ContentCard = styled.div`
  width: 100%;
  max-width: 1080px;
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
`;

const Title = styled.h1`
  font-size: 24px;
  margin: 0 0 8px;
  color: ${colorBlue};
`;

const Subtitle = styled.p`
  margin: 0 0 20px;
  color: ${colorText};
  opacity: 0.9;
`;

const Form = styled.form`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  margin-bottom: 20px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FieldGroup = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
`;

const FieldLabel = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${colorText};
`;

const FieldInput = styled.input`
  height: 38px;
  border: 1px solid #d8e0ef;
  border-radius: 8px;
  padding: 0 12px;
  outline: none;
  font-size: 14px;

  &:focus {
    border-color: ${colorBlue};
  }
`;

const FieldError = styled.small`
  color: #d7344a;
  font-size: 12px;
`;

const Actions = styled.div`
  display: flex;
  align-items: flex-end;
`;

const SubmitButton = styled.button`
  height: 38px;
  padding: 0 20px;
  border: 0;
  border-radius: 8px;
  background: ${colorBlue};
  color: #fff;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ApiError = styled.div`
  background: #fff1f2;
  border: 1px solid #fecdd3;
  color: #9f1239;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
`;

const SummaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
`;

const SummaryItem = styled.span`
  background: #f5f8ff;
  border: 1px solid #dde6fa;
  border-radius: 20px;
  padding: 6px 12px;
  font-size: 13px;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
`;

const TrackingCard = styled.article`
  border: 1px solid #e7edf9;
  border-radius: 10px;
  padding: 12px;
  background: #fff;
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;

  h3 {
    margin: 0;
    font-size: 14px;
    color: ${colorText};
  }
`;

const StatusRow = styled.div`
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const FinalizadoChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${({ $done }) => ($done ? '#0f5132' : '#7c2d12')};
  background: ${({ $done }) => ($done ? '#d1e7dd' : '#ffedd5')};
  border: 1px solid ${({ $done }) => ($done ? '#badbcc' : '#fed7aa')};
`;

const TipoNotaChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${({ $devolucao }) => ($devolucao ? '#7f1d1d' : '#0f2a68')};
  background: ${({ $devolucao }) => ($devolucao ? '#fee2e2' : '#eaf1ff')};
  border: 1px solid ${({ $devolucao }) => ($devolucao ? '#fecaca' : '#d4e1ff')};
`;

const StatusChip = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  color: ${colorBlue};
  background: #edf3ff;
  border: 1px solid #d4e1ff;
  border-radius: 999px;
  padding: 3px 8px;
`;

const CardData = styled.div`
  display: grid;
  gap: 6px;
  font-size: 13px;
  color: ${colorText};
`;

const DivergenciaBox = styled.div`
  margin-top: 6px;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid #fee2e2;
  background: #fff1f2;
  color: #881337;
`;

const MediaSection = styled.div`
  margin-top: 12px;
`;

const MediaTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: ${colorText};
  margin-bottom: 6px;
`;

const MediaGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 8px;
`;

const MediaThumbLink = styled.a`
  display: block;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #dbe5fb;
  background: #f8faff;
`;

const MediaThumb = styled.img`
  width: 100%;
  height: 96px;
  object-fit: cover;
  display: block;
`;

const MediaLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  border: 1px solid #dbe5fb;
  border-radius: 8px;
  padding: 6px 8px;
  font-size: 12px;
  color: ${colorBlue};
  text-decoration: none;
  background: #f8faff;
`;
